import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpParams,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  Component,
  Injectable,
  NgModule,
  type Signal,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal,
  type TemplateRef,
  contentChild,
  DestroyRef
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { Page } from '@app/admin/model/spring-pageable';
import { AuthService } from '@app/components/login.component';
import { ConfigModule, appConfigService } from '@app/config';
import { NTAPCommonModule } from '@ntap/common';
import {
  AuthRequestOptions,
  type CoreConfig,
  FilterViewProviderDirective,
  ListItemProviderDirective,
  ListViewProviderDirective,
  NTAPCoreModule,
  PopupOpenerService
} from '@ntap/core';
import {
  NTAPPrimengModule,
  PrimeNGPopupListViewComponent
} from '@ntap/primeng';
import { AngularQueryDevtools } from '@tanstack/angular-query-devtools-experimental';
import {
  injectInfiniteQuery,
  injectQuery,
  injectQueryClient
} from '@tanstack/angular-query-experimental';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogModule
} from 'primeng/dynamicdialog';
import { type OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import {
  ScrollerModule,
  type ScrollerScrollIndexChangeEvent
} from 'primeng/scroller';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { StyleClassModule } from 'primeng/styleclass';
import { TooltipModule } from 'primeng/tooltip';
import {
  Subject,
  catchError,
  debounceTime,
  filter,
  finalize,
  first,
  interval,
  lastValueFrom,
  of,
  pairwise,
  skip,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import { webSocket } from 'rxjs/webSocket';
import { AppConfigModule } from './config/app.config.module';
import type { Notification } from './notification.util';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-notification-item',
  template: `
    <div class="flex flex-column gap-2">
      <div class="flex flex-column gap-1">
        <span class="font-bold">Poruka</span>
        <span>{{ notification.message }}</span>
      </div>
      @if (notification.details; as details) {
      <div class="flex flex-column gap-1">
        <span class="font-bold">Detalji</span>
        <pre style="font-family: inherit; white-space: pre-wrap;">{{
          details
        }}</pre>
      </div>
      }
      <div class="flex flex-column gap-1">
        <span class="font-bold">Obavijest stvorena</span>
        <span class="font-light">{{
          notification.created | date : 'HH:mm dd.MM.yyyy.'
        }}</span>
      </div>
      <div class="flex flex-column gap-1">
        @if (notification.url; as url) {
        <a class="font-bold" target="__blank" [href]="url"
          >Poveznica na entitet</a
        >
        }
      </div>
      @if (entities().length > 0) {
      <p-button
        label="Pogledaj entitete obavijesti"
        [disabled]="isShowEntitiesDisabled()"
        (onClick)="openEntities()"
      />
      }
    </div>
  `
})
export class NotificationItemComponent {
  private dynamicDialogConfig = inject(DynamicDialogConfig);
  private notificationService = inject(NotificationService);
  private popupOpenerService = inject(PopupOpenerService);
  private dialogService = inject(DialogService);

  public notification: Notification =
    this.dynamicDialogConfig.data.notification;

  public notificationEntitiesQuery = injectQuery(() => ({
    queryKey: ['notifications', this.notification.id],
    queryFn: async () => {
      return lastValueFrom(
        this.notificationService.getNotificationEntities(this.notification.id)
      );
    },
    staleTime: 1000 * 5
  }));

  public entities = computed(
    () => this.notificationEntitiesQuery.data()?.entities ?? []
  );

  public isShowEntitiesDisabled = computed(() => {
    if (
      this.notificationEntitiesQuery.isLoading() ||
      this.notificationEntitiesQuery.isError()
    ) {
      return true;
    }
    return this.entities().length === 0;
  });

  public notificationString = computed(() => {
    return JSON.stringify(this.notificationEntitiesQuery.data(), null, 2);
  });

  public openEntities() {
    this.dialogService.open(PrimeNGPopupListViewComponent, {});

    const entityIds =
      this.notificationEntitiesQuery
        .data()
        ?.entities.map((entity) => entity.entityId) ?? [];

    const viewName = this.notificationEntitiesQuery.data()?.listItemViewName;

    this.popupOpenerService.setPopupState({
      viewName,
      viewState: {
        $filterCriteria: [
          {
            operator: 'IN',
            value: entityIds,
            $fqan: `${this.notification.entityType}~id`
          }
        ]
      }
    });
    this.popupOpenerService.openOrClosePopup(true);
  }
}

@Component({
  selector: 'app-notification-list-item',
  template: `
    <div
      class="flex-row w-full p-2 hover:surface-200 cursor-pointer"
      (click)="openItem()"
    >
      <div class="flex flex-row">
        <p
          [ngClass]="{
            'font-bold': !notification().read,
            'font-light': notification().read
          }"
          style="justify-content: start; align-items: center;margin-bottom: 0;"
        >
          {{ notification().message }}
        </p>
        @if (!notification().read) {
        <div
          [style]="{
            height: '1rem',
            width: '1.6rem',
            marginTop: 'auto',
            marginBottom: 'auto',
            paddingLeft: '0.6rem'
          }"
          class="border-circle bg-blue-500"
        ></div>
        }
      </div>
      <span>
        {{ notification().created | date : 'HH:mm dd.MM.yyyy.' }}
      </span>
    </div>
  `
})
export class NotificationListItemComponent {
  public notification = input.required<Notification>();
  public notificationOpened = output<Notification>();

  private dialogService = inject(DialogService);
  private queryClient = injectQueryClient();

  public openItem() {
    this.dialogService.open(NotificationItemComponent, {
      data: {
        notification: this.notification()
      }
    });
    this.queryClient.setQueryData(['notifications', this.notification().id], {
      ...this.notification(),
      read: true
    });
    this.queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }
}

@Component({
  selector: 'app-notification-items',
  template: `
    @if (data().length === 0) {
    <div class="text-center">
      <ng-content />
    </div>
    }

    <p-scroller
      [style]="{ height: '25rem' }"
      class="h-full"
      [items]="data()"
      [itemSize]="70"
      [delay]="50"
      (onScrollIndexChange)="onLazyLoadMyItems($event)"
    >
      <ng-template pTemplate="item" let-notification>
        <app-notification-list-item [notification]="notification" />
      </ng-template>
    </p-scroller>

    @if (notificationType() === undefined) {
    <div class="flex py-3 justify-content-between">
      <p-button
        label="Obriši sve obavijesti"
        (onClick)="clearNotification($event, true)"
      />
      <p-button
        [disabled]="isClearDisabled()"
        label="Označi sve kao pročitano"
        (onClick)="clearNotification($event, false)"
      />
    </div>
    }
  `
})
export class NotificationItemsComponent {
  public notificationType = input<string | string[]>();

  public noItemsTemplate =
    contentChild<TemplateRef<HTMLElement>>('noElementsTemplate');

  private notificationService = inject(NotificationService);
  private confirmationService = inject(ConfirmationService);

  private notifications = injectNotificationsQuery(this.notificationType);

  private unreadNotificationsQuery = injectUnreadNotificationsQuery();

  public isClearDisabled = computed(() => {
    if (
      (this.notificationType() !== undefined &&
        this.notificationType() !== 'all') ||
      this.unreadNotificationsQuery.isError() ||
      this.unreadNotificationsQuery.isLoading()
    ) {
      return true;
    }
    return this.unreadNotificationsQuery.data() === 0;
  });

  public data = computed(() => {
    return (
      this.notifications.data()?.pages.flatMap((page) => page?.content) ?? []
    );
  });

  public onLazyLoadMyItems(event: ScrollerScrollIndexChangeEvent) {
    const currentElements =
      this.notifications.data()?.pages.flatMap((page) => page?.content)
        .length ?? 0;
    if (
      currentElements - 1 <= event.last &&
      this.notifications.hasNextPage() &&
      !this.notifications.isFetchingNextPage()
    ) {
      this.notifications.fetchNextPage();
    }
  }
  public clearNotification(target: unknown, isDelete: boolean) {
    this.confirmationService.confirm({
      target: target as EventTarget,
      message: `Jeste li sigurni da želite ${
        isDelete
          ? 'obrisati sve svoje obavijesti'
          : 'označiti sve obavijesti kao pročitane'
      }?`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.notificationService.clearNotifications(isDelete);
      }
    });
  }
}

@Component({
  selector: 'app-notification-overlay',
  template: `
    <a
      pButton
      type="button"
      class="p-button-rounded p-button-text text-gray-200"
      (click)="op.toggle($event)"
      #opTarget
    >
      <i class="pi pi-bell text-xl" pBadge [value]="unreadNotifications()"></i>
    </a>

    <p-overlayPanel
      [style]="{ width: '35rem', height: '31rem' }"
      (onHide)="isOverlayOpen.set(false)"
      (onShow)="isOverlayOpen.set(true)"
      #op
    >
      @if (isOverlayOpen()) {
      <app-notification-items>
        <p>Trenutno nemate obavijesti</p>
      </app-notification-items>
      }
    </p-overlayPanel>

    <!-- <angular-query-devtools initialIsOpen /> -->
  `
})
export class NotificationOverlayComponent {
  @ViewChild('op')
  public overlaypanel: OverlayPanel;

  @ViewChild('opTarget')
  public overlaypanelTargetButton: HTMLButtonElement;

  public isOverlayOpen = signal(false);

  // Potrebno samo da se inicijalizira
  private wsService = inject(WSNotificationConnectionService);

  private unreadNotificationsQuery = injectUnreadNotificationsQuery();

  public unreadNotifications = computed(() =>
    String(this.unreadNotificationsQuery.data() ?? 0)
  );
}

@Injectable({
  providedIn: 'root'
})
export class WSNotificationConnectionService {
  private config = inject(appConfigService);
  private authService = inject(AuthService);

  private queryClient = injectQueryClient();

  private refreshSocket = webSocket<
    { refresh: boolean } | { userId: number } | string
  >({
    url: `${this.config.wsHost}/notifications`
  });

  private refresh$ = new Subject<void>();

  constructor() {
    const sendAuth = new Subject<void>();

    sendAuth
      .pipe(switchMap(() => this.authService.validUser$))
      .subscribe((user) => {
        this.refreshSocket.next({
          userId: user.userId.value
        });
      });

    const connectToWs = new Subject<void>();

    connectToWs
      .pipe(
        debounceTime(300),
        switchMap(() => {
          const r = this.refreshSocket.pipe(
            finalize(() => connectToWs.next()),
            catchError(() => {
              connectToWs.next();
              return of('pong');
            }),

            filter((message) => message !== 'pong')
          );

          sendAuth.next();
          return r;
        }),
        debounceTime(500)
      )
      .subscribe({
        next: () => {
          this.queryClient.invalidateQueries({
            queryKey: ['notifications']
          });
        }
      });

    connectToWs.next();
    interval(10_000).subscribe(() => this.refreshSocket.next('ping'));
  }

  public get refresh() {
    return this.refresh$;
  }
}

export function injectNotificationsQuery(
  entityType?: Signal<string | string[] | undefined>
) {
  const notificationService = inject(NotificationService);
  const type = computed(() => {
    if (entityType) {
      return entityType() ?? 'all';
    }
    return 'all';
  });
  return injectInfiniteQuery(() => ({
    queryKey: ['notifications', type()],
    queryFn: async ({ pageParam, queryKey }) => {
      return lastValueFrom(
        notificationService.getNotificationsPage(pageParam, queryKey[1])
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: Page<Notification> | undefined) => {
      if (!lastPage || lastPage.last) {
        return undefined;
      }
      return lastPage.pageable.offset + lastPage.pageable.pageSize;
    },
    staleTime: 1000 * 60 * 5
  }));
}

function injectUnreadNotificationsQuery() {
  const notificationService = inject(NotificationService);
  const messageService = inject(MessageService);
  const destroyRef = inject(DestroyRef);

  const query = injectQuery(() => ({
    queryKey: ['notifications', 'unread'],
    refetchInterval: 1000 * 60 * 2,
    queryFn: async () =>
      lastValueFrom(notificationService.getUnreadMessagesCount())
  }));

  const destroySubject = new Subject<void>();
  destroyRef.onDestroy(() => destroySubject.next());

  toObservable(query.data as Signal<number>)
    .pipe(
      takeUntil(destroySubject),
      skip(1),
      filter((count) => count !== undefined),
      pairwise(),
      filter(([oldValue, newValue]) => newValue > oldValue),
      tap(() => {
        messageService.add({
          severity: 'info',
          summary: 'Imate nove obavijesti',
          detail: 'Provjerite pretinac obavijesti'
        });
      })
    )
    .subscribe();

  return query;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private config = inject(appConfigService);
  private httpClient = inject(HttpClient);
  private queryClient = injectQueryClient();

  public notificationsUrl = `${this.config.apiHost}notification`;

  public getNotificationsPage(offset: number, entityType?: string | string[]) {
    let params = new HttpParams();
    params = params.set('offset', offset);
    if (entityType && entityType !== 'all') {
      if (Array.isArray(entityType)) {
        for (const e of entityType) {
          params = params.append('entityType', e);
        }
      } else {
        params = params.set('entityType', entityType);
      }
    }

    return this.httpClient.get<Page<Notification>>(this.notificationsUrl, {
      ...new AuthRequestOptions(),
      params
    });
  }

  public getUnreadMessagesCount() {
    return this.httpClient.get<number>(
      `${this.notificationsUrl}/unread`,
      new AuthRequestOptions()
    );
  }

  public getNotificationEntities(notificationId: number) {
    return this.httpClient.get<Notification>(
      `${this.notificationsUrl}/${notificationId}`,
      new AuthRequestOptions()
    );
  }

  public clearNotifications(isDelete: boolean, entityType?: string) {
    this.httpClient
      .put(
        `${this.notificationsUrl}/clear`,
        { delete: isDelete, entityType },
        new AuthRequestOptions()
      )
      .pipe(first())
      .subscribe({
        next: () => {
          this.queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
      });
  }
}

@NgModule({
  declarations: [
    NotificationOverlayComponent,
    NotificationListItemComponent,
    NotificationItemComponent,
    NotificationItemsComponent
  ],
  exports: [
    NotificationOverlayComponent,
    NotificationListItemComponent,
    NotificationItemsComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    StyleClassModule,
    AppConfigModule,
    ScrollPanelModule,
    DialogModule,
    OverlayPanelModule,
    ScrollerModule,
    ButtonModule,
    TooltipModule,
    DynamicDialogModule,
    BadgeModule,
    AppConfigModule,
    ScrollingModule,

    ConfigModule.forRoot({} as CoreConfig),
    NTAPCoreModule.forRoot({} as CoreConfig),
    NTAPCommonModule.forRoot({} as CoreConfig),
    NTAPPrimengModule.forRoot(),

    AngularQueryDevtools
  ],
  providers: [
    ListItemProviderDirective,
    ListViewProviderDirective,
    DialogService,
    PopupOpenerService,
    FilterViewProviderDirective,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class NotificationModule {}
